// =============================================================================
// SimSense Marketplace — Become a Seller Page
// =============================================================================
// OT-291: Onboarding funnel for users who want to sell/share setups.
// Shows tier breakdown, eligibility progress, and application flow.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import "./BecomeSellerPage.css";

// Mock eligibility data — will be replaced by real API
interface SellerEligibility {
  communitySetupsShared: number;
  avgRating: number;
  totalDownloads: number;
  iRating: number;
  daysActive: number;
  applicationStatus: "none" | "pending" | "approved" | "rejected";
  rejectionReason?: string;
}

const MOCK_ELIGIBILITY: SellerEligibility = {
  communitySetupsShared: 1,
  avgRating: 0,
  totalDownloads: 0,
  iRating: 0,
  daysActive: 5,
  applicationStatus: "none",
};

// Thresholds for Verified Creator eligibility
const THRESHOLDS = {
  communitySetups: 3,
  avgRating: 4.0,
  totalDownloads: 50,
  iRating: 2000,
  daysActive: 30,
};

function ProgressItem({ label, current, target, unit }: { label: string; current: number; target: number; unit?: string }) {
  const met = current >= target;
  const pct = Math.min(100, (current / target) * 100);
  return (
    <div className={`progress-item ${met ? "met" : ""}`}>
      <div className="progress-item-header">
        <span className="progress-item-label">{label}</span>
        <span className="progress-item-value">
          {current}{unit} / {target}{unit}
          {met && <span className="progress-check">&#10003;</span>}
        </span>
      </div>
      <div className="progress-bar-bg">
        <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function BecomeSellerPage() {
  const { user, isPro } = useAuth();
  const navigate = useNavigate();
  const [applicationText, setApplicationText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Mock — replace with real API hook
  const eligibility = MOCK_ELIGIBILITY;

  const allMet =
    eligibility.communitySetupsShared >= THRESHOLDS.communitySetups &&
    eligibility.avgRating >= THRESHOLDS.avgRating &&
    eligibility.totalDownloads >= THRESHOLDS.totalDownloads &&
    eligibility.iRating >= THRESHOLDS.iRating &&
    eligibility.daysActive >= THRESHOLDS.daysActive;

  const handleApply = () => {
    if (!applicationText.trim()) return;
    setSubmitting(true);
    // TODO: POST /api/marketplace/creator/apply
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 1000);
  };

  return (
    <div className="become-seller-page">
      {/* Breadcrumbs */}
      <div className="breadcrumbs">
        <Link to="/marketplace">Marketplace</Link>
        <span className="bc-sep">/</span>
        <span className="bc-active">Become a Seller</span>
      </div>

      <div className="seller-hero">
        <h1>Sell Your Setups on SimSense</h1>
        <p>
          Share your telemetry-proven setups with the community — or sell them
          to earn revenue. Every setup on SimSense is backed by real lap data.
        </p>
      </div>

      {/* Tier Cards */}
      <div className="seller-tiers">
        {/* Community Sharer */}
        <div className="tier-card tier-community">
          <div className="tier-badge tier-badge-community">Tier 1</div>
          <h2>Community Sharer</h2>
          <p className="tier-desc">
            Share free setups with the SimSense community. Every setup must be
            linked to a telemetry session you actually drove.
          </p>
          <ul className="tier-requirements">
            <li className={isPro ? "req-met" : ""}>
              {isPro ? "✓" : "○"} SimSense Pro subscription
            </li>
            <li className={user?.iracingId ? "req-met" : ""}>
              {user?.iracingId ? "✓" : "○"} Linked iRacing account
            </li>
            <li>○ At least 1 uploaded telemetry session</li>
          </ul>
          <div className="tier-features">
            <span>Free setups only</span>
            <span>Telemetry proof required</span>
            <span>No earnings</span>
          </div>
          {isPro ? (
            <button
              className="tier-cta tier-cta-community"
              onClick={() => navigate("/marketplace/dashboard")}
            >
              Share Your First Setup
            </button>
          ) : (
            <Link to="/pricing" className="tier-cta tier-cta-upgrade">
              Upgrade to Pro to Start
            </Link>
          )}
        </div>

        {/* Verified Creator */}
        <div className="tier-card tier-verified">
          <div className="tier-badge tier-badge-verified">Tier 2</div>
          <h2>Verified Creator</h2>
          <p className="tier-desc">
            Sell setups for $0.50–$50. Get a verified badge, profile page,
            and earnings dashboard with Stripe payouts. 80/20 revenue split.
          </p>
          <ul className="tier-requirements">
            <li>All Community Sharer requirements, plus:</li>
            <li className={eligibility.communitySetupsShared >= THRESHOLDS.communitySetups ? "req-met" : ""}>
              {eligibility.communitySetupsShared >= THRESHOLDS.communitySetups ? "✓" : "○"}{" "}
              {THRESHOLDS.communitySetups}+ community setups shared
            </li>
            <li className={eligibility.avgRating >= THRESHOLDS.avgRating ? "req-met" : ""}>
              {eligibility.avgRating >= THRESHOLDS.avgRating ? "✓" : "○"}{" "}
              {THRESHOLDS.avgRating}+ average rating
            </li>
            <li className={eligibility.totalDownloads >= THRESHOLDS.totalDownloads ? "req-met" : ""}>
              {eligibility.totalDownloads >= THRESHOLDS.totalDownloads ? "✓" : "○"}{" "}
              {THRESHOLDS.totalDownloads}+ total downloads
            </li>
            <li className={eligibility.iRating >= THRESHOLDS.iRating ? "req-met" : ""}>
              {eligibility.iRating >= THRESHOLDS.iRating ? "✓" : "○"}{" "}
              {THRESHOLDS.iRating.toLocaleString()}+ iRating
            </li>
            <li className={eligibility.daysActive >= THRESHOLDS.daysActive ? "req-met" : ""}>
              {eligibility.daysActive >= THRESHOLDS.daysActive ? "✓" : "○"}{" "}
              {THRESHOLDS.daysActive}+ days active on platform
            </li>
          </ul>
          <div className="tier-features">
            <span>Set your own prices</span>
            <span>Verified badge</span>
            <span>Profile page</span>
            <span>Earnings dashboard</span>
          </div>
          {eligibility.applicationStatus === "pending" ? (
            <div className="tier-status tier-status-pending">
              Application under review
            </div>
          ) : eligibility.applicationStatus === "approved" ? (
            <button
              className="tier-cta tier-cta-verified"
              onClick={() => navigate("/marketplace/dashboard")}
            >
              Open Creator Workshop
            </button>
          ) : eligibility.applicationStatus === "rejected" ? (
            <div className="tier-status tier-status-rejected">
              Application not approved
              {eligibility.rejectionReason && (
                <span className="rejection-reason">
                  {eligibility.rejectionReason}
                </span>
              )}
            </div>
          ) : allMet ? (
            <div className="tier-apply-section">
              {submitted ? (
                <div className="tier-status tier-status-pending">
                  Application submitted! We'll review it shortly.
                </div>
              ) : (
                <>
                  <p className="apply-prompt">
                    You meet all requirements! Tell us why you want to sell setups:
                  </p>
                  <textarea
                    className="apply-textarea"
                    placeholder="I've been building setups for 3 years and want to share my work with the community..."
                    value={applicationText}
                    onChange={(e) => setApplicationText(e.target.value)}
                    maxLength={500}
                  />
                  <button
                    className="tier-cta tier-cta-verified"
                    onClick={handleApply}
                    disabled={submitting || !applicationText.trim()}
                  >
                    {submitting ? "Submitting..." : "Apply to Become a Creator"}
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="tier-cta-disabled">
              Meet all requirements to apply
            </div>
          )}
        </div>

        {/* Setup Shop */}
        <div className="tier-card tier-shop">
          <div className="tier-badge tier-badge-shop">Tier 3</div>
          <h2>Setup Shop</h2>
          <p className="tier-desc">
            Full branded storefront with team management, setup packs, analytics,
            and premium placement. For established setup businesses.
          </p>
          <ul className="tier-requirements">
            <li>By invitation or application</li>
            <li>Existing customer base required</li>
            <li>Brand verification by SimSense team</li>
          </ul>
          <div className="tier-features">
            <span>Branded storefront</span>
            <span>Team members</span>
            <span>Setup packs</span>
            <span>Priority placement</span>
            <span>Advanced analytics</span>
          </div>
          <a href="mailto:support@simsense.app?subject=Setup%20Shop%20Application" className="tier-cta tier-cta-shop">
            Contact Us to Apply
          </a>
        </div>
      </div>

      {/* Progress Tracker (only show if Pro and not yet verified) */}
      {isPro && eligibility.applicationStatus === "none" && !allMet && (
        <section className="eligibility-progress">
          <h2>Your Progress Toward Verified Creator</h2>
          <p className="progress-subtitle">
            Meet all thresholds below to unlock your application.
          </p>
          <div className="progress-grid">
            <ProgressItem
              label="Community Setups Shared"
              current={eligibility.communitySetupsShared}
              target={THRESHOLDS.communitySetups}
            />
            <ProgressItem
              label="Average Rating"
              current={eligibility.avgRating}
              target={THRESHOLDS.avgRating}
              unit="★"
            />
            <ProgressItem
              label="Total Downloads"
              current={eligibility.totalDownloads}
              target={THRESHOLDS.totalDownloads}
            />
            <ProgressItem
              label="iRating"
              current={eligibility.iRating}
              target={THRESHOLDS.iRating}
            />
            <ProgressItem
              label="Days Active"
              current={eligibility.daysActive}
              target={THRESHOLDS.daysActive}
            />
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="seller-faq">
        <h2>Common Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h3>What file types can I upload?</h3>
            <p>
              Raw iRacing .sto setup files only. No zip files or archives.
              Each setup variant is uploaded individually.
            </p>
          </div>
          <div className="faq-item">
            <h3>How do setup packs work?</h3>
            <p>
              You can bundle 1–4 setup variants (Race Normal, Tight, Loose,
              Qualifying) for the same car and track into a single listing
              with one price.
            </p>
          </div>
          <div className="faq-item">
            <h3>What's the revenue split?</h3>
            <p>
              Creators keep 80% of every sale. SimSense takes 20% for
              platform hosting, payment processing, and infrastructure.
            </p>
          </div>
          <div className="faq-item">
            <h3>Can setups be reported or removed?</h3>
            <p>
              Yes. Buyers can flag setups for review. Flagged setups go to
              our moderation queue — we review each case manually. No
              automatic removals.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
